"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IntelligenceReports() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: logosRef,
    isVisible: logosVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const testimonials = [{
    name: "SARAH JENKINS",
    role: "OWNER, VACAVILLE BAKERY",
    year: "EST. 2022",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_bakery_owner.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_bakery_owner.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_bakery_owner.webp-wvc-sizes",
    excerpt: "AI Guyz transformed how we handle orders. We stopped missing calls during the morning rush and our booking volume doubled overnight."
  }, {
    name: "MARCUS VANCE",
    role: "FOUNDER, VANCE PLUMBING",
    year: "EST. 2023",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_plumbing_founder.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_plumbing_founder.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_plumbing_founder.png-wvc-sizes",
    excerpt: "The 24/7 AI answering service is a game changer. I no longer worry about losing leads while I'm out on a job site."
  }, {
    name: "PRIYA SHARMA",
    role: "DIRECTOR, SHARMA DENTAL",
    year: "EST. 2021",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_dental_director.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_dental_director.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_dental_director.png-wvc-sizes",
    excerpt: "Professional, reliable, and local. AI Guyz built us a premium brand identity that finally matches the quality of our care."
  }, {
    name: "DAVID MILLER",
    role: "MANAGER, MILLER AUTO",
    year: "EST. 2020",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_auto_manager.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_auto_manager.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_auto_manager.webp-wvc-sizes",
    excerpt: "Automating our scheduling was the best decision we made. The system is seamless and our customers love the instant booking."
  }, {
    name: "ELENA RODRIGUEZ",
    role: "OWNER, RODRIGUEZ LANDSCAPING",
    year: "EST. 2022",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_landscaping_owner.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_landscaping_owner.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_landscaping_owner.webp-wvc-sizes",
    excerpt: "My business feels like a major corporation now. The AI handles everything while I focus on the actual landscaping work."
  }, {
    name: "JAMAL WRIGHT",
    role: "FOUNDER, WRIGHT CONSULTING",
    year: "EST. 2022",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_consulting_founder.webp",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_consulting_founder.webp-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_consulting_founder.webp-wvc-sizes",
    excerpt: "The father-son team at AI Guyz really cares about local growth. They treat my business like it's their own."
  }, {
    name: "AIKO TANAKA",
    role: "OWNER, TANAKA DESIGN",
    year: "EST. 2019",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_design_owner.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_design_owner.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_design_owner.png-wvc-sizes",
    excerpt: "I was skeptical about AI, but the results speak for themselves. My growth is on autopilot and I'm finally reclaiming my time."
  }, {
    name: "RAFAEL MORENO",
    role: "FOUNDER, MORENO LOGISTICS",
    year: "EST. 2023",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_logistics_founder.png",
    avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_logistics_founder.png-wvc-srcset",
    avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_logistics_founder.png-wvc-sizes",
    excerpt: "AI Guyz bet on the future of local business, and I'm glad I did too. Their toolkit is essential for any modern company."
  }];
  const employers = [{
    name: "Bakery",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_bakery.png"
  }, {
    name: "Plumbing",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_plumbing.png"
  }, {
    name: "Dental",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_dental.png"
  }, {
    name: "Auto",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_auto.png"
  }, {
    name: "Landscaping",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_landscaping.png"
  }, {
    name: "Consulting",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_consulting.png"
  }, {
    name: "Design",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_design.webp"
  }, {
    name: "Logistics",
    file: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/logo_logistics.png"
  }];
  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = 340;
    const index = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.min(Math.max(index, 0), testimonials.length - 1));
  };
  const scrollBy = (dir: "prev" | "next") => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = 340;
    el.scrollBy({
      left: dir === "next" ? cardWidth : -cardWidth,
      behavior: "smooth"
    });
  };
  return <section data-section-id="532" id="intelligence-reports" className="relative overflow-hidden bg-background py-24 md:py-32">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-[-10%] h-[600px] w-[600px] rounded-lg opacity-60 blur-3xl" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)",
      opacity: 0.15
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div ref={headerRef} className={`flex flex-col items-center transition-all duration-700 ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <h2 className="font-default text-center text-4xl leading-[1.05] tracking-tight text-foreground md:text-6xl lg:text-[80px]">Mission Success Briefings</h2>
        </div>

        <div ref={quoteRef} className={`mt-20 transition-all duration-700 ${quoteVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="relative grid grid-cols-1 lg:grid-cols-12">
            <div className="relative lg:col-span-7 lg:col-start-3">
              <Quote aria-hidden="true" className="absolute -left-4 -top-16 h-[140px] w-[140px] -scale-x-100 text-primary md:-left-10 md:-top-24 md:h-[200px] md:w-[200px]" strokeWidth={1} />

              <blockquote className="relative font-default text-2xl leading-snug text-foreground md:text-3xl lg:text-[36px] lg:leading-[1.25]">
                AI Guyz did not just build us a website -- they built us a growth engine. We stopped missing calls and started booking more clients immediately.
              </blockquote>

              <div className="mt-10 flex items-center gap-5">
                <SafeHydrate><Avatar className="h-14 w-14 rounded-lg ring-1 ring-border">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_bakery_owner.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_bakery_owner.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704929/img/client_bakery_owner.webp-wvc-sizes" alt="Sarah Jenkins" />
                  <AvatarFallback className="rounded-lg bg-muted text-muted-foreground">
                    SJ
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div className="flex flex-col gap-1">
                  <span className="font-default text-lg font-semibold text-foreground">
                    Sarah Jenkins
                  </span>
                  <span className="font-mono text-xs tracking-wider text-muted-foreground">
                    OWNER, VACAVILLE BAKERY
                  </span>
                </div>
                <span className="ml-2 inline-flex items-center rounded-lg bg-primary px-2.5 py-1 font-mono text-[11px] font-semibold tracking-wider text-primary-foreground">
                  CLIENT SINCE 2022
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24">
          <div className="flex items-end justify-between gap-6 pb-6">
            
            <div className="flex items-center gap-6">
              <span className="font-mono text-xs tracking-wider text-muted-foreground">
                <span className="text-foreground">
                  {String(activeIndex + 1).padStart(2, "0")}
                </span>{" "}
                / {String(testimonials.length).padStart(2, "0")}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => scrollBy("prev")} aria-label="Previous testimonial" className="h-10 w-10 rounded-lg border-border bg-transparent text-primary hover:bg-primary hover:text-primary-foreground">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => scrollBy("next")} aria-label="Next testimonial" className="h-10 w-10 rounded-lg border-border bg-transparent text-primary hover:bg-primary hover:text-primary-foreground">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div ref={scrollerRef} onScroll={handleScroll} className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {testimonials.map((t, i) => <article data-index={i} key={i} className="flex w-[320px] shrink-0 snap-start flex-col gap-5 rounded-lg border border-border bg-card p-6 transition-colors duration-300 hover:border-primary/50">
                <div className="flex items-center gap-3">
                  <SafeHydrate><Avatar className="h-10 w-10 rounded-lg ring-1 ring-border">
                    <AvatarImage src={t.avatar} data-wvc-srcset={t.avatarSrcset} data-wvc-sizes={t.avatarSizes} alt={t.name} />
                    <AvatarFallback className="rounded-lg bg-muted text-xs text-muted-foreground">
                      {t.name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <span className="font-mono text-[10px] tracking-wider text-primary">
                    {t.year}
                  </span>
                </div>
                <p className="font-default text-base leading-snug text-card-foreground line-clamp-3">
                  {t.excerpt}
                </p>
                <div className="mt-auto flex flex-col gap-1 border-t border-border pt-4">
                  <span className="font-default text-sm font-semibold text-foreground">
                    {t.name}
                  </span>
                  <span className="font-mono text-[11px] tracking-wider text-muted-foreground">
                    {t.role}
                  </span>
                </div>
              </article>)}
          </div>
        </div>

        <div ref={logosRef} className={`mt-24 transition-all duration-700 ${logosVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          
        </div>
      </div>
    </section>;
}